import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { authApi, mergeSubscriptionParameters } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { useState } from 'react'
import {
  TextField,
  Button,
  Alert,
  Box,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Tooltip,
  InputLabel,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  HelpOutline,
} from '@mui/icons-material'

const createRegisterSchema = (t: (key: string) => string) => z.object({
  surname: z
    .string()
    .min(1, t('auth.validation.surnameRequired'))
    .max(255, t('auth.validation.maxLength')),
  first_name: z
    .string()
    .min(1, t('auth.validation.firstNameRequired'))
    .max(255, t('auth.validation.maxLength')),
  middle_name: z
    .string()
    .max(255, t('auth.validation.maxLength'))
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .min(1, t('auth.validation.emailRequired'))
    .email(t('auth.validation.emailInvalid'))
    .max(255, t('auth.validation.maxLength')),
  password: z
    .string()
    .min(8, t('auth.validation.passwordMin')),
  password_confirmation: z
    .string()
    .min(8, t('auth.validation.passwordMin')),
  code: z
    .string()
    .min(1, t('auth.validation.codeRequired')),
  privacy_policy: z
    .boolean()
    .refine((val) => val === true, {
      message: t('auth.validation.privacyPolicyRequired'),
    }),
  consent_to_mailing: z
    .boolean()
    .optional(),
  birthday: z
    .string()
    .optional()
    .or(z.literal('')),
}).refine((data) => data.password === data.password_confirmation, {
  message: t('auth.validation.passwordMismatch'),
  path: ['password_confirmation'],
})

export function RegisterForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)

  const registerSchema = createRegisterSchema(t)
  type RegisterFormData = z.infer<typeof registerSchema>

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      privacy_policy: false,
      consent_to_mailing: false,
    },
  })

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError(null)
      setIsLoading(true)
      
      const registerData = {
        surname: data.surname,
        first_name: data.first_name,
        middle_name: data.middle_name || undefined,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        code: data.code,
        privacy_policy: data.privacy_policy,
        consent_to_mailing: data.consent_to_mailing,
        birthday: data.birthday || undefined,
      }

      const response = await authApi.register(registerData)
      const user = await mergeSubscriptionParameters(response.user)
      setAuth(user, response.token, {
        expiresIn: response.expires_in,
        rememberMe: false,
      })
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error.response?.data?.message || error.message || t('auth.registerError')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      <Controller
        name="surname"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.surname')}
            fullWidth
            error={!!errors.surname}
            helperText={errors.surname?.message}
          />
        )}
      />

      <Controller
        name="first_name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.firstName')}
            fullWidth
            error={!!errors.first_name}
            helperText={errors.first_name?.message}
          />
        )}
      />

      <Controller
        name="middle_name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={`${t('auth.middleName')} (${t('common.optional')})`}
            fullWidth
            error={!!errors.middle_name}
            helperText={errors.middle_name?.message}
          />
        )}
      />

      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.email')}
            type="email"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.password')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            error={!!errors.password}
            helperText={errors.password?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Controller
        name="password_confirmation"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('auth.passwordConfirmation')}
            type={showPasswordConfirmation ? 'text' : 'password'}
            fullWidth
            error={!!errors.password_confirmation}
            helperText={errors.password_confirmation?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                    edge="end"
                    aria-label="toggle password confirmation visibility"
                  >
                    {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <InputLabel htmlFor="code" sx={{ fontWeight: 600 }}>
            {t('auth.invitationCode')}
          </InputLabel>
          <Tooltip title={t('auth.invitationCodeTooltip')} arrow>
            <HelpOutline fontSize="small" color="action" sx={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="code"
              fullWidth
              error={!!errors.code}
              helperText={errors.code?.message}
            />
          )}
        />
      </Box>

      <Controller
        name="birthday"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={`${t('auth.birthday')} (${t('common.optional')})`}
            type="date"
            fullWidth
            error={!!errors.birthday}
            helperText={errors.birthday?.message}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: new Date().toISOString().split('T')[0],
            }}
          />
        )}
      />

      <Controller
        name="privacy_policy"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                color="primary"
              />
            }
            label={t('auth.privacyPolicyAccept')}
          />
        )}
      />
      {errors.privacy_policy && (
        <Alert severity="error" sx={{ mt: -2, mb: 1 }}>
          {errors.privacy_policy.message}
        </Alert>
      )}

      <Controller
        name="consent_to_mailing"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value || false}
                color="primary"
              />
            }
            label={t('auth.consentToMailing')}
          />
        )}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        sx={{
          mt: 1,
          py: 1.5,
        }}
      >
        {isLoading ? t('common.loading') : t('auth.register')}
      </Button>
    </Box>
  )
}
